import {
  MutableRefObject, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { CircularProgress, Tooltip } from '@material-ui/core';
import { GridRowData } from '@material-ui/x-grid';

import {
  PAYMENT_METHOD_TYPES, PAYMENT_TYPE, PaymentMethodModel,
} from 'src/models/PaymentModel';
import { getPaymentMethodAction } from 'src/redux/actions/paymentActions';
import { Store, useAppDispatch } from 'src/redux/Store';

const HOVER_DELAY = 150;
const PAYMENT_TYPE_FIELD = 'payments.payment_type';
const PAYMENT_INFO_ID_FIELD = 'payments.payment_info_id';

interface Props {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  rows: GridRowData[];
  reloadKey?: unknown;
}

interface AnchorPosition {
  top: number;
  left: number;
}

const getPaymentMethodLabel = (paymentMethod: PaymentMethodModel) => {
  if (paymentMethod.kind === 'card') {
    return `${paymentMethod.maskedCardNumber} · ${paymentMethod.expirationData}`;
  }

  return [
    paymentMethod.bankName,
    paymentMethod.bankHolderName,
    paymentMethod.account,
  ].filter(Boolean).join(' · ');
};

const DatagridPaymentMethodTooltip = (props: Props) => {
  const { containerRef, rows, reloadKey } = props;
  const dispatch = useAppDispatch();
  const organisationId = useSelector((state: Store) => state.currentOrganisation.id);

  const [anchor, setAnchor] = useState<AnchorPosition | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const cache = useRef<Map<number, PaymentMethodModel | null>>(new Map());
  const pendingIds = useRef<Set<number>>(new Set());
  const hoveredId = useRef<number | null>(null);
  const hoverPosition = useRef<AnchorPosition | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const isTooltipVisible = useRef(false);
  const moveFrame = useRef<number | null>(null);
  const requestGeneration = useRef(0);
  const rowsRef = useRef<GridRowData[]>(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    hoveredId.current = null;
    isTooltipVisible.current = false;
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setOpen(false);
  }, [reloadKey]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const generation = requestGeneration.current + 1;
    requestGeneration.current = generation;
    cache.current.clear();
    pendingIds.current.clear();
    hoveredId.current = null;

    const clearHoverTimer = () => {
      if (hoverTimer.current !== null) {
        window.clearTimeout(hoverTimer.current);
        hoverTimer.current = null;
      }
    };

    const closeTooltip = () => {
      isTooltipVisible.current = false;
      setOpen(false);
    };

    const hideTooltip = () => {
      clearHoverTimer();
      setLoading(false);
      closeTooltip();
    };

    const getPointerPosition = (event: MouseEvent): AnchorPosition => {
      const containerRect = container.getBoundingClientRect();
      return {
        top: event.clientY - containerRect.top,
        left: event.clientX - containerRect.left,
      };
    };

    const openTooltip = (position: AnchorPosition) => {
      isTooltipVisible.current = true;
      setAnchor(position);
      setOpen(true);
    };

    const showIfStillHovered = (paymentId: number) => {
      if (hoveredId.current !== paymentId) return;
      const paymentMethodInfo = cache.current.get(paymentId);
      const pointerPosition = hoverPosition.current;
      setLoading(false);
      if (!paymentMethodInfo || !pointerPosition) {
        closeTooltip();
        return;
      }
      setPaymentMethod(paymentMethodInfo);
      openTooltip(pointerPosition);
    };

    const showLoadingIfStillHovered = (paymentId: number) => {
      if (hoveredId.current !== paymentId) return;
      const pointerPosition = hoverPosition.current;
      if (!pointerPosition) return;
      setPaymentMethod(null);
      setLoading(true);
      openTooltip(pointerPosition);
    };

    const requestPaymentMethod = async (paymentId: number) => {
      const row = rowsRef.current.find((gridRow) => Number(gridRow.id) === paymentId);
      const paymentTypeHint = row?.[PAYMENT_TYPE_FIELD] as PAYMENT_TYPE | undefined;
      const paymentInfoIdHint = row?.[PAYMENT_INFO_ID_FIELD] as number | null | undefined;
      if (paymentTypeHint && !PAYMENT_METHOD_TYPES.includes(paymentTypeHint)) {
        closeTooltip();
        return;
      }
      if (paymentInfoIdHint === null) {
        closeTooltip();
        return;
      }
      if (cache.current.has(paymentId)) {
        showIfStillHovered(paymentId);
        return;
      }

      showLoadingIfStillHovered(paymentId);
      if (pendingIds.current.has(paymentId)) return;

      pendingIds.current.add(paymentId);
      const resolvedPaymentMethod = await dispatch(
        getPaymentMethodAction(paymentId, paymentTypeHint, paymentInfoIdHint),
      );
      pendingIds.current.delete(paymentId);
      if (requestGeneration.current !== generation) return;

      cache.current.set(paymentId, resolvedPaymentMethod);
      showIfStillHovered(paymentId);
    };

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const rowElement = target?.closest<HTMLElement>('[role="row"][data-id]') ?? null;
      const paymentId = rowElement ? Number(rowElement.getAttribute('data-id')) : 0;
      if (!paymentId) {
        hoveredId.current = null;
        hideTooltip();
        return;
      }
      if (paymentId === hoveredId.current) return;

      hoveredId.current = paymentId;
      hoverPosition.current = getPointerPosition(event);
      hideTooltip();
      hoverTimer.current = window.setTimeout(
        () => requestPaymentMethod(paymentId),
        HOVER_DELAY,
      );
    };

    const handleMouseLeave = () => {
      hoveredId.current = null;
      hideTooltip();
    };

    const handleMouseMove = (event: MouseEvent) => {
      hoverPosition.current = getPointerPosition(event);
      if (!isTooltipVisible.current || moveFrame.current !== null) return;
      moveFrame.current = window.requestAnimationFrame(() => {
        moveFrame.current = null;
        const pointerPosition = hoverPosition.current;
        if (!isTooltipVisible.current || !pointerPosition) return;
        setAnchor(pointerPosition);
      });
    };

    const handleMouseDown = () => {
      hoveredId.current = null;
      hideTooltip();
    };

    const handleScroll = () => {
      hoveredId.current = null;
      hideTooltip();
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('scroll', handleScroll, true);

    return () => {
      if (requestGeneration.current === generation) {
        requestGeneration.current += 1;
      }
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('scroll', handleScroll, true);
      if (moveFrame.current !== null) {
        window.cancelAnimationFrame(moveFrame.current);
        moveFrame.current = null;
      }
      clearHoverTimer();
    };
  }, [containerRef, dispatch, organisationId]);

  const getTooltipTitle = () => {
    if (loading) {
      return <CircularProgress size={16} />;
    }
    if (paymentMethod) {
      return getPaymentMethodLabel(paymentMethod);
    }
    return '';
  };

  return (
    <Tooltip
      open={open && (loading || !!paymentMethod)}
      title={getTooltipTitle()}
      placement="right-start"
      disableFocusListener
      disableTouchListener>
      <div
        style={{
          position: 'absolute',
          top: anchor?.top ?? 0,
          left: anchor?.left ?? 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }} />
    </Tooltip>
  );
};

export default DatagridPaymentMethodTooltip;
